import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import { IMetaState } from "./types"

const initialState: IMetaState = {
  isChartFetching: false,
  isTableFetching: false,
  isChartLoading: true,   // true при старте — показываем лоадер до загрузки данных
  isTableLoading: true,   // true при старте — показываем лоадер до загрузки данных
  isAllExpandedLoading: false,
  isCompare: false,
  isChartCompareFetching: false,
  isTableCompareFetching: false,
  isFilterUpdate: false,
  currency: "",
  isNotParamsFilterVisible: false,
  blocks: {},
  isAllBlocksFetching: false,
  isModalTableFetching: false,
  isModalChartFething: false,
  // Timestamp до которого игнорируются попытки установить isTableFetching: true
  // Используется для предотвращения перезагрузки отчёта после сохранения визуальных параметров метрики
  skipTableFetchUntil: 0,
  // Полноэкранный режим — скрывает header и sidebar
  isFullscreen: false,
}
const initialStateBlock = {
  isChartFetching: true,
  isTableFetching: true,
  isChartLoading: true,
  isTableLoading: true,
  isAllExpandedLoading: false,
  isCompare: false,
  isChartCompareFetching: false,
  isTableCompareFetching: false,
  isFilterUpdate: false,
  currency: "",
  isNotParamsFilterVisible: false,
  isAllBlocksFetching: false,
  isModalTableFetching: false,
  isModalChartFething: false,
  skipTableFetchUntil: 0,
  isFullscreen: false,
}

export const metaSlice = createSlice({
  name: "meta",
  initialState: initialState,
  reducers: {
    setAllBlocksFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isAllBlocksFetching = value
      } else {
        state.isAllBlocksFetching = value
      }
    },
    setDashboardsBlocksInitialState: (
      state,
      action: PayloadAction<string[]>
    ) => {
      const newBlocksValues: { [key: string]: Omit<IMetaState, "blocks"> } = {}
      const ids = action.payload
      ids.forEach((el) => {
        newBlocksValues[el] = initialStateBlock
      })
      state.blocks = { ...state.blocks, ...newBlocksValues }
    },
    setMetaChartFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        if (!state.blocks[blockId]) {
          state.blocks[blockId] = {
            ...initialStateBlock,
            isChartFetching: false,
          }
        }
        // КРИТИЧЕСКИ ВАЖНО: создаем новый объект блока для правильного уведомления Redux
        state.blocks[blockId] = {
          ...state.blocks[blockId],
          isChartFetching: value,
        }
      } else {
        state.isChartFetching = value
      }
    },
    setMetaModalChartFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isModalChartFething = value
      } else {
        state.isModalChartFething = value
      }
    },
    setMetaTableFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      // Блокируем установку isTableFetching: true если skipTableFetchUntil ещё не истёк
      // Это предотвращает перезагрузку отчёта после сохранения визуальных параметров метрики
      if (value && state.skipTableFetchUntil > Date.now()) {
        return
      }
      if (blockId) {
        if (!state.blocks[blockId]) {
          state.blocks[blockId] = {
            ...initialStateBlock,
            isTableFetching: false,
          }
        }
        // КРИТИЧЕСКИ ВАЖНО: создаем новый объект блока для правильного уведомления Redux
        state.blocks[blockId] = {
          ...state.blocks[blockId],
          isTableFetching: value,
        }
      } else {
        state.isTableFetching = value
      }
    },
    setMetaModalTableFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isModalTableFetching = value
      } else {
        state.isModalTableFetching = value
      }
    },
    setAllDataFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      // Блокируем установку isTableFetching: true если skipTableFetchUntil ещё не истёк
      if (value && state.skipTableFetchUntil > Date.now()) {
        return
      }
      if (blockId) {
        state.blocks[blockId].isChartFetching = value
        state.blocks[blockId].isTableFetching = value
      } else {
        state.isChartFetching = value
        state.isTableFetching = value
      }
    },
    // Устанавливает timestamp до которого игнорируются попытки установить isTableFetching: true
    // Также сбрасывает isTableFetching в false чтобы предотвратить текущую загрузку
    setSkipTableFetchUntil: (state, action: PayloadAction<number>) => {
      state.skipTableFetchUntil = action.payload
      // Сбрасываем флаги чтобы предотвратить загрузку, которая уже могла быть инициирована
      state.isTableFetching = false
      state.isTableCompareFetching = false
    },
    setMetaChartLoading: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId } = action.payload
      if (blockId) {
        state.blocks[blockId].isChartLoading = value
      } else {
        state.isChartLoading = value
      }
    },

    setMetaTableLoading: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isTableLoading = value
      } else {
        state.isTableLoading = value
      }
    },
    setIsAllExpandedLoading: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isAllExpandedLoading = value
      } else {
        state.isAllExpandedLoading = value
      }
    },
    setMetaCompare: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isCompare = value
        // Устанавливаем флаги загрузки только при включении режима сравнения
        if (value) {
          state.blocks[blockId].isChartFetching = true
          state.blocks[blockId].isChartCompareFetching = true
          state.blocks[blockId].isTableFetching = true
          state.blocks[blockId].isTableCompareFetching = true
        }
      } else {
        state.isCompare = value
        // Устанавливаем флаги загрузки только при включении режима сравнения
        if (value) {
          state.isChartFetching = true
          state.isChartCompareFetching = true
          state.isTableFetching = true
          state.isTableCompareFetching = true
        }
      }
    },
    setMetaCompareChartFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isChartCompareFetching = value
      } else {
        state.isChartCompareFetching = value
      }
    },
    setMetaCompareTableFetching: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId = null } = action.payload
      if (blockId) {
        state.blocks[blockId].isTableCompareFetching = value
      } else {
        state.isTableCompareFetching = value
      }
    },
    setMetaFilterUpdate: (
      state,
      action: PayloadAction<{ value: boolean; blockId?: string }>
    ) => {
      const { value, blockId } = action.payload
      if (blockId) {
        state.blocks[blockId].isFilterUpdate = value
      } else {
        state.isFilterUpdate = value
      }
    },
    setMetaCurrency: (state, action: PayloadAction<string>) => {
      state.currency = action.payload
    },
    setIsNotParamsFilterVisible: (state, action: PayloadAction<boolean>) => {
      state.isNotParamsFilterVisible = action.payload
    },
    /**
     * Очищает мета-состояние для конкретного блока дашборда.
     * Вызывается при удалении блока для предотвращения утечки памяти.
     */
    clearBlockMetaState: (state, action: PayloadAction<string>) => {
      const blockId = action.payload
      if (state.blocks && state.blocks[blockId]) {
        delete state.blocks[blockId]
      }
    },
    /**
     * Устанавливает полноэкранный режим.
     * При включении скрывает header и sidebar.
     */
    setMetaFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isFullscreen = action.payload
    },
  },
})

export const metaSliceReducer = metaSlice.reducer
export const metaSliceActions = metaSlice.actions
